import React, { useState, useRef, useMemo } from 'react';
import { Box, Button, Grid2, Typography, Tabs, Tab, Paper, CircularProgress, Accordion, AccordionSummary, AccordionDetails, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useGetDocumentName, useGetLmsGrades, useGetLmsSubject, useGetLmsTags, useGetAiLms, useGetVersionContent } from '../../assets/nodeApi/getApi/getAdminApi';
import { postAilmsGenerate, postSaveDraftAiLms, postAilmsUploadImage } from '../../assets/nodeApi/postApi/postAdminApi';
import { patchFinalizeAiLmsVersion } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField';
import { marked } from 'marked';

const sharedContentStyles = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    mt: 2,
    mb: 1,
    fontWeight: 600,
    color: '#111827',
  },
  '& h1': { fontSize: '24px' },
  '& h2': { fontSize: '20px' },
  '& h3': { fontSize: '18px' },
  '& p': {
    mb: 2,
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#374151',
  },
  '& ul, & ol': {
    mb: 2,
    pl: 3,
    color: '#374151',
  },
  '& li': {
    mb: 0.5,
    fontSize: '14px',
    lineHeight: 1.6,
  },
  '& strong': {
    fontWeight: 600,
    color: '#111827',
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& blockquote': {
    borderLeft: '4px solid #E5E7EB',
    pl: 2,
    py: 0.5,
    my: 2,
    color: '#6B7280',
    fontStyle: 'italic',
    backgroundColor: '#F9FAFB'
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    mb: 2,
    display: 'block',
    overflowX: 'auto',
  },
  '& th, & td': {
    border: '1px solid #E5E7EB',
    p: 1,
    textAlign: 'left',
    fontSize: '14px',
  },
  '& th': {
    backgroundColor: '#F9FAFB',
    fontWeight: 600,
  },
  '& pre': {
    background: '#F9FAFB',
    p: 2,
    borderRadius: '4px',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '13px',
    color: '#374151',
  },
  '& code': {
    background: '#F9FAFB',
    px: 0.5,
    py: 0.2,
    borderRadius: '4px',
    fontSize: '13px',
    color: '#d63384',
    wordBreak: 'break-word',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    margin: '16px auto',
    borderRadius: '8px',
  }
};

const AddAiLms = () => {
  const [tabValue, setTabValue] = useState(0);
  const [contentState, setContentState] = useState("initial");
  const [editorContent, setEditorContent] = useState("");
  const [versionId, setVersionId] = useState(null);
  const [compareParams, setCompareParams] = useState({ 
    topic: "", 
    subject: "", 
    grade: "",
    student_level: "",
    teacher_efficacy: "",
    tag: "",
    doc_name: ""
  });
  const [compareVersionIndex, setCompareVersionIndex] = useState(0);

  const formikRef = useRef();
  const quillRef = useRef(null);
  const { showToaster } = useToaster();

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'align': [] }],
          ['link', 'image'],
          ['clean']
        ],
        handlers: {
          image: () => {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();

            input.onchange = async () => {
              const file = input.files[0];
              if (file) {
                showToaster('Compressing and uploading image...', '', 'info');

                const compressImage = (f) => {
                  return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(f);
                    reader.onload = (event) => {
                      const img = new Image();
                      img.src = event.target.result;
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        const MAX_WIDTH = 1200;
                        const MAX_HEIGHT = 1200;
                        let width = img.width;
                        let height = img.height;

                        if (width > height) {
                          if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                          }
                        } else {
                          if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                          }
                        }

                        canvas.width = width;
                        canvas.height = height;
                        ctx.drawImage(img, 0, 0, width, height);

                        canvas.toBlob((blob) => {
                          resolve(new File([blob], f.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                          }));
                        }, 'image/jpeg', 0.7);
                      };
                    };
                  });
                };

                try {
                  const compressedFile = await compressImage(file);
                  const formData = new FormData();
                  formData.append('file', compressedFile);

                  const response = await postAilmsUploadImage(formData);

                  if (response?.success) {
                    const url = response.data.url;
                    const quill = quillRef.current.getEditor();
                    const range = quill.getSelection(true);
                    const index = range ? range.index : quill.getLength();
                    quill.insertEmbed(index, 'image', url);
                    quill.setSelection(index + 1);
                    showToaster('Image uploaded successfully', '', 'success');
                  } else {
                    showToaster('Failed to upload image', '', 'error');
                  }
                } catch (error) {
                  console.error('Image upload error:', error);
                  showToaster('Failed to upload image', '', 'error');
                }
              }
            };
          }
        }
      }
    };
  }, [showToaster]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const { data: subjects, isLoading: isSubjectsLoading } = useGetLmsSubject();
  const { data: grades, isLoading: isGradesLoading } = useGetLmsGrades();
  const { data: tags, isLoading: isTagsLoading } = useGetLmsTags();
  const { data: docName, isLoading: isDocNameLoading } = useGetDocumentName();

  const subjectOptions =
    subjects?.data
      ?.filter((item) => item.status === "active")
      ?.map((item) => ({
        label: item.subject,
        value: String(item.subject),
        id: String(item.id),
      })) || [];

  const gradeOptions = grades?.data?.map((item) => ({
    label: item.name,
    value: String(item.numeric_value),
    id: String(item.numeric_value),
  })) || [];

  const tagOptions = tags?.data?.map((item) => ({
    label: item.tag,
    value: String(item.tag),
    id: String(item.id),
  })) || [];

  const docNameOptions = docName?.data?.filter((item) => item.status === 1)?.map((item) => ({
    label: item.docName,
    value: String(item.docName),
    id: String(item.id),
  })) || [];

  const StudentUnderstandingOptions = [
    { value: 'Below Average', id: '1', label: 'Below Average' },
    { value: 'Average', id: '2', label: 'Average' },
    { value: 'Above Average', id: '3', label: 'Above Average' }
  ];

  const TeacherEfficacyOptions = [
    { value: 'Low', id: '1', label: 'Low' },
    { value: 'Medium', id: '2', label: 'Medium' },
    { value: 'High', id: '3', label: 'High' },
  ];

  const { data: compareListResponse, isFetching: isFetchingList } = useGetAiLms({
    topic: compareParams.topic,
    subject: compareParams.subject,
    grade: compareParams.grade,
    limit: 100
  }, {
    enabled: contentState === 'compare' && !!compareParams.topic
  });

  const matchingLms = compareListResponse?.data?.contents?.find(item => 
    item.topic === compareParams.topic &&
    item.subject === compareParams.subject &&
    item.grade === compareParams.grade &&
    item.student_level === compareParams.student_level &&
    item.teacher_efficacy === compareParams.teacher_efficacy &&
    item.tag === compareParams.tag &&
    item.doc_name === compareParams.doc_name
  );

  const compareLmsId = matchingLms?._id;

  const { data: compareVersionResponse, isFetching: isFetchingVersions } = useGetVersionContent(compareLmsId, {
    enabled: !!compareLmsId && contentState === 'compare'
  });

  const isComparing = isFetchingList || isFetchingVersions;
  const versions = compareVersionResponse?.data?.versions || [];
  const currentCompareVersion = versions[compareVersionIndex];

  const handleCompare = () => {
    const values = formikRef.current?.values;
    if (!values) return;

    const selectedSubject = subjectOptions.find(opt => opt.value === String(values.subjectId) || opt.id === String(values.subjectId));
    const subjectIdNum = selectedSubject ? Number(selectedSubject.id) : Number(values.subjectId);

    const selectedGrade = gradeOptions.find(opt => opt.value === String(values.gradeId) || opt.id === String(values.gradeId));
    const gradeIdNum = selectedGrade ? Number(selectedGrade.id) : Number(values.gradeId);

    setCompareParams({
      topic: values.topicId,
      subject: subjectIdNum,
      grade: gradeIdNum,
      student_level: values.studentLevelId,
      teacher_efficacy: values.teacherEfficacyId,
      tag: values.tagsId,
      doc_name: values.docName
    });
    setCompareVersionIndex(0);
    setContentState('compare');
  };

  const validationSchema = Yup.object().shape({
    gradeId: Yup.mixed().required('Grade is required').test('not-empty', 'Grade is required', (v) => v !== '' && v != null),
    subjectId: Yup.mixed().required('Subject is required').test('not-empty', 'Subject is required', (v) => v !== '' && v != null),
    topicId: Yup.mixed().required('Topic is required').test('not-empty', 'Topic is required', (v) => v !== '' && v != null),
    tagsId: Yup.mixed().required('Tags are required').test('not-empty', 'Tags are required', (v) => v !== '' && v != null),
    studentLevelId: Yup.mixed().required('Student Understanding Level is required').test('not-empty', 'Level is required', (v) => v !== '' && v != null),
    teacherEfficacyId: Yup.mixed().required('Teacher Efficacy is required').test('not-empty', 'Efficacy is required', (v) => v !== '' && v != null),
    docName: Yup.string().trim().required('Doc Name is required'),
  });

  const initialValues = {
    gradeId: '',
    subjectId: '',
    topicId: '',
    tagsId: '',
    studentLevelId: '',
    teacherEfficacyId: '',
    docName: '',
  };

  const generateAiMutation = useMutation({
    mutationFn: ({ grade, subject, payload }) => postAilmsGenerate(grade, subject, payload),
  });

  const saveDraftMutation = useMutation({
    mutationFn: (payload) => postSaveDraftAiLms(payload),
    onSuccess: (data) => {
      showToaster('Draft saved successfully!', '', 'success');
      const vId = data?.data?.data?.version?._id || data?.data?.version?._id || data?.version?._id;
      if (vId) setVersionId(vId);
    },
    onError: (err) => {
      console.error(err);
      showToaster(err?.response?.data?.message || 'Failed to save draft', '', 'error');
    }
  });

  const publishMutation = useMutation({
    mutationFn: (id) => patchFinalizeAiLmsVersion(id),
    onSuccess: () => {
      showToaster('Published successfully!', '', 'success');
      setEditorContent("");
      formikRef.current?.resetForm();
      setVersionId(null);
      setContentState('initial');
    },
    onError: (err) => {
      console.error(err);
      showToaster(err?.response?.data?.message || 'Failed to publish', '', 'error');
    }
  });

  const handlePublish = () => {
    if (!versionId) {
      showToaster('Please save as draft first', '', 'error');
      return;
    }
    publishMutation.mutate(versionId);
  };

  const handleSaveDraft = () => {
    const values = formikRef.current?.values;
    if (!values) return;

    const selectedSubject = subjectOptions.find(opt => opt.value === String(values.subjectId) || opt.id === String(values.subjectId));
    const subjectIdNum = selectedSubject ? Number(selectedSubject.id) : Number(values.subjectId);

    const selectedGrade = gradeOptions.find(opt => opt.value === String(values.gradeId) || opt.id === String(values.gradeId));
    const gradeIdNum = selectedGrade ? Number(selectedGrade.id) : Number(values.gradeId);

    const payload = {
      topic: values.topicId,
      student_level: values.studentLevelId,
      teacher_efficacy: values.teacherEfficacyId,
      generated_content: editorContent,
      doc_name: values.docName,
      subject: subjectIdNum,
      grade: gradeIdNum,
      tag: values.tagsId
    };

    saveDraftMutation.mutate(payload);
  };

  const handleSubmit = (values) => {
    const tabLabels = ["Script"];
    const selectedFormat = tabLabels[tabValue] || "Script";

    const payload = {
      topic: values.topicId,
      tags: values.tagsId,
      format: selectedFormat,
      student_level: values.studentLevelId,
      teacher_efficacy: values.teacherEfficacyId,
      doc_name: values.docName
    };

    generateAiMutation.mutate({
      grade: values.gradeId,
      subject: values.subjectId,
      payload
    }, {
      onSuccess: (data) => {
        showToaster('Content generated successfully!', '', 'success');

        const generatedContent = data?.data?.generated_content || data?.data?.content || data?.data?.data || data?.data || data?.content || data?.generated_content || "";
        const finalContent = typeof generatedContent === 'string' ? generatedContent : JSON.stringify(generatedContent);

        const parser = new DOMParser();
        const decoded = parser.parseFromString(finalContent, 'text/html').documentElement.textContent;

        const htmlContent = marked.parse(decoded || finalContent, {
          breaks: true,
          gfm: true,
        });

        setEditorContent(htmlContent);
        setContentState('generated');
      },
      onError: (err) => {
        console.error(err);
        showToaster(err?.response?.data?.message || 'Failed to generate content', '', 'error');
        setContentState('initial');
      }
    });
  };

  const handleEdit = () => {
    setContentState('editing');
  };

  const handleBack = () => {
    setContentState('generated');
  };

  return (
    <Box sx={{
      fontFamily: "'Poppins', sans-serif",
      background: '#fff',
      borderRadius: '16px',
      border: '1px solid #F3F4F6',
      p: { xs: '20px', md: '32px' },
      mx: 'auto',
      maxWidth: '1200px'
    }}>
      {contentState !== 'compare' && (
        <Box>
          <Typography sx={{ fontSize: '24px', fontWeight: 500, color: '#111827', mb: '4px' }}>
            AI LMS
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#4B5563', mb: '32px' }}>
            Create and organize syllabus details for better course planning
          </Typography>
        </Box>
      )}

      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            {contentState !== 'compare' && (
              <>
                <Grid2 container spacing={4} sx={{ mb: 4 }}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Field
                      name="gradeId"
                      component={AutoCompleteField}
                      label="Grade"
                      isRequired
                      options={gradeOptions}
                      loading={isGradesLoading}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Field
                      name="subjectId"
                      component={AutoCompleteField}
                      label="Subject" isRequired
                      options={subjectOptions}
                      loading={isSubjectsLoading}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Field
                      name="topicId"
                      component={TextFormField}
                      label="Topic"
                      isRequired
                      placeholder="Enter Topic"
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Field
                      name="tagsId"
                      component={AutoCompleteField}
                      label="Tags" isRequired
                      options={tagOptions}
                      loading={isTagsLoading}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Field
                      name="studentLevelId"
                      component={AutoCompleteField}
                      label="Student Understanding Level" isRequired
                      options={StudentUnderstandingOptions}
                      disableSort
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Field
                      name="teacherEfficacyId"
                      component={AutoCompleteField}
                      label="Teacher Efficacy" isRequired
                      options={TeacherEfficacyOptions}
                      disableSort
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Field
                      name="docName"
                      component={AutoCompleteField}
                      label="Doc Name" isRequired
                      options={docNameOptions}
                      loading={isDocNameLoading}
                    />
                  </Grid2>
                </Grid2>

                <Box sx={{ borderBottom: 1, borderColor: '#F3F4F6', mb: 3 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontSize: '15px',
                        fontWeight: 400,
                        color: '#4B5563',
                        minWidth: 'auto',
                        mr: 4,
                        px: 0,
                      },
                      '& .Mui-selected': {
                        color: '#4169E1 !important',
                        fontWeight: 500,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#4169E1',
                        height: '3px',
                        borderTopLeftRadius: '3px',
                        borderTopRightRadius: '3px',
                      }
                    }}
                  >
                    <Tab label="Script" />
                  </Tabs>
                </Box>
              </>
            )}

            {tabValue === 0 && (
              <Box>
                {contentState === 'initial' && (
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={generateAiMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                    disabled={generateAiMutation.isPending}
                    sx={{
                      background: generateAiMutation.isPending ? '#fff' : '#4169E1',
                      color: generateAiMutation.isPending ? '#0f29bd' : '#fff',
                      textTransform: 'none',
                      borderRadius: '6px',
                      px: 3,
                      py: 1,
                      boxShadow: 'none',
                      '&:hover': { background: '#3154B5', boxShadow: 'none' }
                    }}
                  >
                    {generateAiMutation.isPending ? 'Generating...' : 'Generate Content'}
                  </Button>
                )}

                {contentState === 'generated' && (
                  <Box>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        mb: 2,
                        color: '#111827',
                        fontSize: '14px',
                        lineHeight: 1.6,
                        overflowX: 'auto',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      <Box
                        dangerouslySetInnerHTML={{ __html: editorContent }}
                        sx={sharedContentStyles}
                      />
                    </Paper>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon sx={{ fontSize: '18px' }} />}
                        onClick={handleEdit}
                        sx={{
                          borderColor: '#EBF5FF',
                          background: '#EBF5FF',
                          color: '#4169E1',
                          textTransform: 'none',
                          borderRadius: '6px',
                          px: 3,
                          py: 1,
                          '&:hover': { background: '#DBEAFE', borderColor: '#DBEAFE' }
                        }}
                      >
                        Tap To Edit Content !!
                      </Button>
                    </Box>
                  </Box>
                )}

                {contentState === 'editing' && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Button
                        onClick={handleBack}
                        sx={{
                          textTransform: 'none',
                          color: '#4169E1',
                          fontSize: '14px',
                          padding: 0,
                          '&:hover': { background: 'transparent', textDecoration: 'underline' }
                        }}
                      >
                        <ArrowBackIcon sx={{ color: '#000', mr: 1, fontSize: '20px' }} />
                        Click here to go back !!!
                      </Button>
                    </Box>

                    <Box sx={{
                      mb: 4,
                      '& .quill': {
                        background: '#F9FAFB',
                        borderRadius: '4px',
                        border: '1px solid #E5E7EB',
                      },
                      '& .ql-toolbar': {
                        border: 'none',
                        borderBottom: '1px solid #E5E7EB',
                        background: '#fff',
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                      },
                      '& .ql-container': {
                        border: 'none',
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px',
                        minHeight: '250px',
                        fontSize: '14px',
                        fontFamily: "'Poppins', sans-serif",
                      },
                      '& .ql-editor': {
                        ...sharedContentStyles,
                        minHeight: '250px',
                      }
                    }}>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={editorContent}
                        onChange={setEditorContent}
                        modules={modules}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleCompare}
                        sx={{
                          borderRadius: '6px',
                          borderColor: '#4169E1',
                          color: '#4169E1',
                          textTransform: 'none',
                          px: 4,
                          py: 0.8
                        }}
                      >
                        Compare
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          borderRadius: '6px',
                          background: '#EBF5FF',
                          color: '#4169E1',
                          boxShadow: 'none',
                          textTransform: 'none',
                          px: 4,
                          py: 0.8,
                          '&:hover': { background: '#DBEAFE', boxShadow: 'none' }
                        }}
                      >
                        Send Feedback
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveDraft}
                        disabled={saveDraftMutation.isPending || !!versionId}
                        startIcon={saveDraftMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                          borderRadius: '6px',
                          background: saveDraftMutation.isPending ? '#fff' : '#4169E1',
                          color: saveDraftMutation.isPending ? '#0f29bd' : '#fff',
                          boxShadow: 'none',
                          textTransform: 'none',
                          px: 4,
                          py: 0.8,
                          '&:hover': { background: '#3154B5', boxShadow: 'none' }
                        }}
                      >
                        {saveDraftMutation.isPending ? 'Saving...' : 'Save as Draft'}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handlePublish}
                        disabled={publishMutation.isPending || !versionId}
                        startIcon={publishMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                          borderRadius: '6px',
                          background: publishMutation.isPending ? '#fff' : '#008000',
                          color: publishMutation.isPending ? '#008000' : '#fff',
                          boxShadow: 'none',
                          textTransform: 'none',
                          px: 4,
                          py: 0.8,
                          '&:hover': { background: '#006400', boxShadow: 'none' }
                        }}
                      >
                        {publishMutation.isPending ? 'Publishing...' : 'Publish'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {contentState === 'compare' && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                      <IconButton onClick={() => setContentState('generated')} sx={{ mr: 1, color: '#000' }}>
                        <ArrowBackIcon />
                      </IconButton>
                      <Box>
                        <Typography sx={{ fontSize: '24px', fontWeight: 500, color: '#111827' }}>
                          Compare
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#4B5563' }}>
                          Create and organize syllabus details for better course planning
                        </Typography>
                      </Box>
                    </Box>

                    {isComparing ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Grid2 container spacing={2} sx={{ mb: 4 }}>
                          <Grid2 size={{ xs: 12, md: 6 }}>
                            <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '8px', background: '#F9FAFB', height: '100%' }}>
                              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#111827', mb: 2 }}>
                                Previous Generated Content
                              </Typography>
                              <Box sx={{
                                ...sharedContentStyles,
                                maxHeight: '600px',
                                overflowY: 'auto'
                              }}
                                dangerouslySetInnerHTML={{ __html: currentCompareVersion?.generatedContent || 'No previous content available' }}
                              />
                            </Paper>
                          </Grid2>

                          <Grid2 size={{ xs: 12, md: 6 }}>
                            <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '8px', background: '#F9FAFB', height: '100%', marginTop: { xs: 3, md: 0 } }}>
                              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#111827', mb: 2 }}>
                                Newly Generated Content
                              </Typography>
                              <Box sx={{
                                '& .quill': {
                                  background: '#F9FAFB',
                                  borderRadius: '4px',
                                  border: '1px solid #E5E7EB',
                                },
                                '& .ql-toolbar': {
                                  border: 'none',
                                  borderBottom: '1px solid #E5E7EB',
                                  background: '#fff',
                                  borderTopLeftRadius: '4px',
                                  borderTopRightRadius: '4px',
                                },
                                '& .ql-container': {
                                  border: 'none',
                                  borderBottomLeftRadius: '4px',
                                  borderBottomRightRadius: '4px',
                                  minHeight: '250px',
                                  maxHeight: '600px',
                                  overflowY: 'auto',
                                  fontSize: '14px',
                                  fontFamily: "'Poppins', sans-serif",
                                },
                                '& .ql-editor': {
                                  ...sharedContentStyles,
                                  minHeight: '250px',
                                }
                              }}>
                                <ReactQuill
                                  ref={quillRef}
                                  theme="snow"
                                  value={editorContent}
                                  onChange={setEditorContent}
                                  modules={modules}
                                />
                              </Box>
                            </Paper>
                          </Grid2>
                        </Grid2>

                        {versions.some(v => v.is_final) && versions[0]?.is_final === false && (
                          <Box sx={{ mt: 10 }}>
                            <Typography sx={{ fontSize: '20px', fontWeight: 500, color: '#111827', mb: 1 }}>
                              Approved Versions
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: '#4B5563', mb: 2 }}>
                              List Of Approved versions of AI LMS
                            </Typography>

                            {versions.filter(v => v.is_final).map((v) => (
                              <Accordion key={v._id} elevation={0} sx={{ border: '1px solid #4169E1', borderRadius: '8px !important', '&:before': { display: 'none' }, mb: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4169E1' }} />}>
                                  <Typography sx={{ fontSize: '15px', color: '#111827' }}>
                                    Version {String(v.versionNumber)}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ borderTop: '1px solid #E5E7EB', p: 3 }}>
                                  <Box sx={sharedContentStyles} dangerouslySetInnerHTML={{ __html: v.generatedContent }} />
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AddAiLms;